import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnChangeFn, Row, SortingState } from '@tanstack/react-table';

import { PayrunListItem, PersonaRole } from '@xemplo/gp-types';
import { useGetPayrunListQuery } from '@xemplo/payrun-query';
import { useRoleAuthorisation } from '@xemplo/roles-query';
import { useServerSideState } from '@xemplo/table';
import { buildSortParams } from '@xemplo/url-helper';

import { PAYRUN_TABLE_COLUMNS } from './payrun-base-table.columns';
import { getPayrunDetailPath } from './payrun-base-table.helper';
import * as S from './payrun-base-table.styles';
import { usePayrunColumnVisibility } from './use-payrun-column-visibility';
import { useCombinedPayrunFilters } from './use-payrun-table-filters';

export function PayrunBaseTable() {
  const navigate = useNavigate();

  const { isLoading: roleAuthLoading, isInRole } = useRoleAuthorisation();

  const columnVisibility = usePayrunColumnVisibility();

  const [sorting, setSorting] = useState<SortingState>([
    { desc: false, id: 'milestoneDueDateTimeUtc' },
  ]);
  const handleSortingChange: OnChangeFn<SortingState> = (sorting) => {
    setSorting(sorting);
  };

  const overflowControl = useMemo(() => {
    return isInRole([PersonaRole.CompanyAdmin, PersonaRole.CompanyAccountant])
      ? 950
      : 1600;
  }, [isInRole]);

  const { pagination, onPaginationChange, resetPagination } = useServerSideState();
  const { pageIndex, pageSize } = pagination;

  const filters = useCombinedPayrunFilters();

  useEffect(() => {
    resetPagination();
  }, [filters, resetPagination]);

  const { data, isLoading } = useGetPayrunListQuery({
    requestParams: {
      page: pageIndex + 1,
      per_page: pageSize,
      ...buildSortParams(sorting),
      filter: filters,
    },
  });

  function handleRowClick(e: React.MouseEvent, row: Row<PayrunListItem>) {
    const target = e.target as HTMLElement;
    // If the click is on the checkbox, don't redirect
    if (
      target?.getAttribute('data-role') === 'checkbox' ||
      target?.getAttribute('data-checkbox') === 'true' ||
      target?.getAttribute('type') === 'button'
    ) {
      return;
    }
    navigate(getPayrunDetailPath(row.original));
  }

  if (roleAuthLoading) return null;
  return (
    <S.PayrunListTable
      data={data?.result?.rows ?? []}
      tableOverflowControlMinWidth={overflowControl}
      columns={PAYRUN_TABLE_COLUMNS}
      enablePagination
      isListTable={true}
      manualPagination
      enableRowSelection={false}
      onPaginationChange={onPaginationChange}
      enableSorting
      pageCount={data?.result?.total_Pages ?? 0}
      onSortingChange={handleSortingChange}
      manualSorting
      state={{
        pagination,
        sorting,
        columnVisibility,
      }}
      totalRowCount={data?.result?.total ?? 0}
      isLoading={isLoading}
      onBodyRowClick={handleRowClick}
    />
  );
}

export default PayrunBaseTable;
